import React, { useState, useCallback } from "react";
import {
  Box, Typography, Grid, TextField, MenuItem, Button, IconButton,
  Paper, Chip, CircularProgress, Divider, Tooltip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import "dayjs/locale/es";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import swal from "sweetalert";
import api from "../components/utils/api";
import { useNavigate } from "react-router-dom";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("es");

const TIPOS = ["Mantenimiento", "Limpieza", "Reparacion", "Otros"];

function formatHoras(hs) {
  if (hs === null || hs === undefined) return null;
  const h = Math.floor(hs);
  const min = Math.round((hs - h) * 60);
  if (h === 0) return `${min}min`;
  if (min === 0) return `${h}h`;
  return `${h}h ${min}min`;
}

const LINEA_VACIA = () => ({
  tipoTarea: "",
  horaInicio: null,
  horaFin: null,
  viajeIdaHs:    "",
  viajeIdaMin:   "",
  viajeVueltaHs: "",
  viajeVueltaMin: "",
  descripcion: "",
});

// Convierte horas + minutos (strings) a decimal
function hMinToDecimal(hs, min) {
  return (parseInt(hs) || 0) + (parseInt(min) || 0) / 60;
}

// Detecta solapamiento entre dos pares de horas dayjs
function solapan(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart.isBefore(bEnd) && aEnd.isAfter(bStart);
}

// Devuelve el nro de linea (1-based) con la que solapa, o null si no hay solapamiento
function getConflicto(lineas, idx) {
  const a = lineas[idx];
  if (!a.horaInicio || !a.horaFin) return null;
  for (let j = 0; j < lineas.length; j++) {
    if (j === idx) continue;
    const b = lineas[j];
    if (solapan(a.horaInicio, a.horaFin, b.horaInicio, b.horaFin)) return j + 1;
  }
  return null;
}

function calcHoras(inicio, fin) {
  if (!inicio || !fin) return null;
  const diff = fin.diff(inicio, "minute");
  if (diff <= 0) return null;
  return +(diff / 60).toFixed(2);
}

// ─── Card de una línea ────────────────────────────────────────────────────────
function LineaCard({ linea, idx, total, conflicto, onChange, onDelete }) {
  const hs      = calcHoras(linea.horaInicio, linea.horaFin);
  const completa = linea.tipoTarea && linea.horaInicio && linea.horaFin && hs !== null;

  let badge;
  if (conflicto) {
    badge = (
      <Chip size="small" icon={<WarningAmberIcon sx={{ fontSize: "14px !important" }} />}
        label={`Solapa con línea ${conflicto}`}
        sx={{ bgcolor: "rgba(211,47,47,0.1)", color: "error.main", border: "1px solid", borderColor: "error.light", fontWeight: 500, fontSize: 11 }} />
    );
  } else if (completa) {
    badge = (
      <Chip size="small" icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
        label={`OK · ${formatHoras(hs)}`}
        sx={{ bgcolor: "rgba(46,125,50,0.1)", color: "success.main", border: "1px solid", borderColor: "success.light", fontWeight: 500, fontSize: 11 }} />
    );
  } else {
    badge = (
      <Chip size="small" label="Incompleto"
        sx={{ bgcolor: "action.hover", color: "text.secondary", fontSize: 11 }} />
    );
  }

  return (
    <Paper variant="outlined" sx={{
      p: 2, borderRadius: 2,
      borderColor: conflicto ? "error.light" : completa ? "success.light" : "divider",
      borderWidth: conflicto || completa ? 1.5 : 1,
      transition: "border-color 0.2s",
    }}>
      {/* Header de la card */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
          Línea {idx + 1}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {badge}
          {total > 1 && (
            <Tooltip title="Eliminar línea">
              <IconButton size="small" onClick={() => onDelete(idx)} sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}>
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        <Grid container spacing={1.5}>
          {/* Tipo de tarea */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Tipo de tarea" select size="small" fullWidth required
              value={linea.tipoTarea}
              onChange={e => onChange(idx, "tipoTarea", e.target.value)}
            >
              {TIPOS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>

          {/* Hora inicio */}
          <Grid item xs={6} sm={2}>
            <TimePicker
              label="Hora inicio" ampm={false}
              value={linea.horaInicio}
              onChange={v => onChange(idx, "horaInicio", v)}
              slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
            />
          </Grid>

          {/* Hora fin */}
          <Grid item xs={6} sm={2}>
            <TimePicker
              label="Hora fin" ampm={false}
              value={linea.horaFin}
              onChange={v => onChange(idx, "horaFin", v)}
              slotProps={{ textField: { size: "small", fullWidth: true, required: true,
                error: !!(linea.horaFin && linea.horaInicio && linea.horaFin.isBefore(linea.horaInicio)),
                helperText: (linea.horaFin && linea.horaInicio && linea.horaFin.isBefore(linea.horaInicio)) ? "Fin antes que inicio" : "",
              } }}
            />
          </Grid>

          {/* Viaje ida */}
          <Grid item xs={6} sm={2}>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <TextField size="small" type="number" label="Ida hs" inputProps={{ min: 0 }}
                value={linea.viajeIdaHs}
                onChange={e => onChange(idx, "viajeIdaHs", e.target.value)}
                sx={{ flex: 1 }} />
              <TextField size="small" type="number" label="min" inputProps={{ min: 0, max: 59 }}
                value={linea.viajeIdaMin}
                onChange={e => onChange(idx, "viajeIdaMin", e.target.value)}
                sx={{ flex: 1 }} />
            </Box>
          </Grid>

          {/* Viaje vuelta */}
          <Grid item xs={6} sm={2}>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <TextField size="small" type="number" label="Vuelta hs" inputProps={{ min: 0 }}
                value={linea.viajeVueltaHs}
                onChange={e => onChange(idx, "viajeVueltaHs", e.target.value)}
                sx={{ flex: 1 }} />
              <TextField size="small" type="number" label="min" inputProps={{ min: 0, max: 59 }}
                value={linea.viajeVueltaMin}
                onChange={e => onChange(idx, "viajeVueltaMin", e.target.value)}
                sx={{ flex: 1 }} />
            </Box>
          </Grid>

          {/* Descripción */}
          <Grid item xs={12}>
            <TextField
              label="Descripción del trabajo" size="small" fullWidth multiline minRows={2}
              value={linea.descripcion}
              onChange={e => onChange(idx, "descripcion", e.target.value)}
              placeholder="Detallá el trabajo realizado en esta línea..."
            />
          </Grid>
        </Grid>
      </LocalizationProvider>
    </Paper>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function CargarHoras() {
  const navigate   = useNavigate();
  const userToken  = localStorage.getItem("user-token");
  const user       = localStorage.getItem("nombre-Completo");
  const urlHoras   = api.ordenesHoras;
  const urlOrdenes = api.ordenes;

  // Cabecera
  const [serie,        setSerie]        = useState("");
  const [fecha,        setFecha]        = useState(dayjs().tz(dayjs.tz.guess()));
  const [ot,           setOt]           = useState("");
  const [otSerieError, setOtSerieError] = useState(false);
  const [loading,      setLoading]      = useState(false);

  // Líneas de detalle
  const [lineas, setLineas] = useState([LINEA_VACIA()]);

  // Buscar OT por serie al hacer blur
  const buscarPorSerie = useCallback(async (valor) => {
    if (!valor) return;
    try {
      const res = await fetch(`${urlOrdenes}serie/${valor}`, { headers: { "iron-token": userToken } });
      if (res.ok) {
        const data = await res.json();
        if (data?.ordenTrabajo) setOt(String(data.ordenTrabajo));
      }
    } catch {}
  }, [urlOrdenes, userToken]);

  // Update de un campo de una línea
  const updateLinea = useCallback((idx, field, value) => {
    setLineas(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  }, []);

  const agregarLinea = () => setLineas(prev => [...prev, LINEA_VACIA()]);

  const eliminarLinea = (idx) => {
    setLineas(prev => prev.filter((_, i) => i !== idx));
  };

  // ─── Guardar ───────────────────────────────────────────────────────────────
  const cargarHoras = async () => {
    if (!serie && !ot) { setOtSerieError(true); swal("Error", "Completá al menos la Serie o la Orden de Trabajo", "error"); return; }
    setOtSerieError(false);

    for (let i = 0; i < lineas.length; i++) {
      const l = lineas[i];
      if (!l.tipoTarea)   { swal("Error", `Línea ${i + 1}: tipo de tarea requerido`, "error"); return; }
      if (!l.horaInicio)  { swal("Error", `Línea ${i + 1}: hora de inicio requerida`, "error"); return; }
      if (!l.horaFin)     { swal("Error", `Línea ${i + 1}: hora de fin requerida`, "error"); return; }
      if (l.horaFin.isBefore(l.horaInicio)) { swal("Error", `Línea ${i + 1}: la hora de fin debe ser posterior al inicio`, "error"); return; }
      if (getConflicto(lineas, i)) { swal("Error", `La línea ${i + 1} tiene un solapamiento de horario`, "error"); return; }
    }

    const totalHs = lineas.reduce((acc, l) => acc + (calcHoras(l.horaInicio, l.horaFin) || 0), 0);
    const ok = await swal({
      title: `¿Confirmar ${lineas.length} línea(s)?`,
      text: `Se asignarán ${totalHs.toFixed(2)} hs al usuario ${user}`,
      icon: "warning", buttons: ["Cancelar", "Confirmar"], dangerMode: false,
    });
    if (!ok) return;

    const payload = lineas.map(l => ({
      ordenTrabajo:      ot,
      serie,
      usuario:           user,
      fecha:             fecha.toISOString(),
      horaInicio:        l.horaInicio.toISOString(),
      horaFin:           l.horaFin.toISOString(),
      totalHoras:        calcHoras(l.horaInicio, l.horaFin),
      descripcion:       l.descripcion,
      tipoTarea:         l.tipoTarea,
      tiempoViajeIda:    hMinToDecimal(l.viajeIdaHs,    l.viajeIdaMin),
      tiempoViajeVuelta: hMinToDecimal(l.viajeVueltaHs, l.viajeVueltaMin),
    }));

    setLoading(true);
    try {
      const res = await fetch(`${urlHoras}bulk`, {
        method: "POST",
        body: JSON.stringify({ lineas: payload }),
        headers: { "Content-type": "application/json; charset=UTF-8", "iron-token": userToken },
      });
      setLoading(false);

      if (res.status === 409) {
        const err = await res.json();
        swal("Solapamiento detectado", err.message, "error");
        return;
      }
      if (!res.ok) throw new Error("Error al guardar las horas");

      swal({ title: "Horas guardadas!", icon: "success", timer: 2500, buttons: false });
      navigate("/reporte-horas");
    } catch (err) {
      setLoading(false);
      swal("Error", err.message, "error");
    }
  };

  const hayConflictos   = lineas.some((_, i) => getConflicto(lineas, i));
  const faltaOtOSerie   = !serie && !ot;
  const totalHsResumen = lineas.reduce((acc, l) => acc + (calcHoras(l.horaInicio, l.horaFin) || 0), 0);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 860, mx: "auto" }}>

      {/* Titulo */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
        <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "text.secondary" }}>
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Typography variant="h6" sx={{ color: "text.primary" }}>
          Cargar horas a orden de trabajo
        </Typography>
      </Box>

      {/* ── Cabecera ─────────────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2, bgcolor: "rgba(237,149,25,0.04)", borderColor: "rgba(237,149,25,0.3)" }}>
        <Typography variant="caption" sx={{ color: "#ED9519", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", mb: 1.5 }}>
          Cabecera
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField label="Serie / Equipo" size="small" fullWidth
                value={serie}
                onChange={e => { setSerie(e.target.value); if (e.target.value) setOtSerieError(false); }}
                onBlur={e => buscarPorSerie(e.target.value)}
                error={otSerieError && !serie}
                helperText={otSerieError && !serie ? "Completá Serie o OT" : "Al salir del campo busca la OT automáticamente"}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker label="Fecha" value={fecha} onChange={setFecha}
                slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Orden de trabajo (OT)" size="small" fullWidth
                value={ot}
                onChange={e => { setOt(e.target.value); if (e.target.value) setOtSerieError(false); }}
                error={otSerieError && !ot}
                helperText={otSerieError && !ot ? "Completá Serie o OT" : " "}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {/* ── Líneas de detalle ────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.07em", fontSize: 11 }}>
          Líneas de detalle
        </Typography>
        {lineas.length > 1 && (
          <Typography variant="caption" sx={{ color: hayConflictos ? "error.main" : "text.secondary" }}>
            {hayConflictos ? "⚠ Hay solapamientos" : `Total: ${formatHoras(totalHsResumen)}`}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {lineas.map((linea, idx) => (
          <LineaCard
            key={idx}
            linea={linea}
            idx={idx}
            total={lineas.length}
            conflicto={getConflicto(lineas, idx)}
            onChange={updateLinea}
            onDelete={eliminarLinea}
          />
        ))}
      </Box>

      {/* Botón agregar línea */}
      <Button
        startIcon={<AddIcon />}
        onClick={agregarLinea}
        sx={{
          mt: 1.5, width: "100%", py: 1,
          border: "1.5px dashed", borderColor: "divider",
          color: "text.secondary", borderRadius: 2,
          "&:hover": { borderColor: "#ED9519", color: "#ED9519", bgcolor: "rgba(237,149,25,0.05)" },
        }}
      >
        Agregar línea
      </Button>

      <Divider sx={{ my: 2.5 }} />

      {/* Resumen + botón guardar */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {lineas.length} línea(s) &nbsp;·&nbsp;
            <strong>{formatHoras(totalHsResumen)}</strong> de trabajo
            {(lineas.some(l => hMinToDecimal(l.viajeIdaHs, l.viajeIdaMin) > 0 || hMinToDecimal(l.viajeVueltaHs, l.viajeVueltaMin) > 0)) && (
              <> &nbsp;+&nbsp;
                <strong>
                  {formatHoras(lineas.reduce((a, l) => a + hMinToDecimal(l.viajeIdaHs, l.viajeIdaMin) + hMinToDecimal(l.viajeVueltaHs, l.viajeVueltaMin), 0))}
                </strong> de viaje
              </>
            )}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            Técnico: {user}
          </Typography>
        </Box>

        <Button
          variant="contained"
          endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          disabled={loading || hayConflictos || faltaOtOSerie}
          onClick={cargarHoras}
          sx={{
            bgcolor: "#ED9519", color: "#1a0e00", fontWeight: 700, px: 3,
            "&:hover": { bgcolor: "#c97d0e" },
            "&:disabled": { bgcolor: "action.disabledBackground" },
          }}
        >
          {loading ? "Guardando..." : "Guardar horas"}
        </Button>
      </Box>
    </Box>
  );
}
